import { FC } from 'react';
import { ComponentRendering, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';

import { withTheme } from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import { IntroSectionItem } from 'composables/Product/IntroSection/IntroSectionItem';

import { IntroSectionFields } from './introSectionTypes';

const IntroSection: FC = ({ fields }: { fields: IntroSectionFields }) => {
  const kspDataSource = fields.dataSource;
  const numberOfColumns = Number(fields.numberOfColumns?.value) || 3;

  const eyebrowText = fields.eyebrowText?.value || kspDataSource?.Title?.value || '';
  const title = fields.title?.value || kspDataSource?.Header?.value || '';
  const description = fields.description?.value || kspDataSource?.Text?.value || '';

  const textGradient = fields.textGradient?.value;
  const descriptionFontSize = fields.descriptionFontSize?.value;
  const textShadow = fields.textShadow?.value;

  const gradientTextStyles = textGradient
    ? {
        background: textGradient,
        WebkitBackgroundClip: 'text' as const,
        WebkitTextFillColor: 'transparent',
        color: 'transparent',
      }
    : {};

  const numericFontSize = descriptionFontSize ? Number(descriptionFontSize) : undefined;
  const isValidFontSize =
    numericFontSize !== undefined && Number.isFinite(numericFontSize) && numericFontSize > 0;

  const lineHeights = {
    sm: 1.6,
    md: 1.4,
    lg: 1.2,
  };
  const getLineHeight = (size?: number) => {
    if (!size) return undefined;

    if (size < 1.25) return lineHeights.sm;
    if (size < 2) return lineHeights.md;
    return lineHeights.lg;
  };

  const descriptionFontSizeStyles = isValidFontSize
    ? {
        fontSize: `${numericFontSize}rem`,
        lineHeight: getLineHeight(numericFontSize),
      }
    : {};

  const headlineLineHeight = textGradient ? 1.1 : undefined;

  const shadowClass = textShadow ? 'drop-shadow-lg' : '';
  const itemCount = fields.items?.length ?? 0;

  const numberOfColumnsClass = `grid-cols-1 md:grid-cols-2 ${
    itemCount > 3 ? `lg:grid-cols-${numberOfColumns}` : 'lg:grid-cols-3'
  }`;

  return (
    <div className="bg-theme relative overflow-hidden">
      <div className="container relative flex max-w-6xl flex-col items-center overflow-hidden">
        {eyebrowText && (
          <Eyebrow
            text={eyebrowText as string}
            align="center"
            className={`mb-5 items-center drop-shadow-md ${shadowClass}`}
            style={gradientTextStyles}
          />
        )}
        {title && (
          <h2
            className={`heading-2 text-center ${shadowClass}`}
            style={{
              ...gradientTextStyles,
              lineHeight: headlineLineHeight,
            }}
            dangerouslySetInnerHTML={{ __html: title as string }}
          />
        )}
        {description && (
          <div
            className={`paragraph-large text-center ${shadowClass}`}
            style={{
              ...gradientTextStyles,
              ...descriptionFontSizeStyles,
            }}
            dangerouslySetInnerHTML={{ __html: description as string }}
          />
        )}
        {fields.items?.length > 0 && (
          <ul className={`mt-6 grid w-full gap-4 ${numberOfColumnsClass}`}>
            {fields.items.map((el) => (
              <IntroSectionItem key={el.id} {...el} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export const getStaticProps = async (rendering: ComponentRendering<IntroSectionFields>) => {
  try {
    const { fields: dataSource, params = {} } = rendering;
    if (!dataSource) {
      throw new Error('No fields found for Intro Section');
    }
    const fields = {
      ...dataSource,
      eyebrowText: {
        ...dataSource.eyebrowText,
        value: sanitize((dataSource.eyebrowText?.value as string) || ''),
      },
      title: {
        ...dataSource.title,
        value: sanitize((dataSource.title?.value as string) || ''),
      },
      description: {
        ...dataSource.description,
        value: sanitize((dataSource.description?.value as string) || ''),
      },
      descriptionFontSize: {
        ...dataSource.descriptionFontSize,
        value: sanitize((dataSource.descriptionFontSize?.value as string) || ''),
      },
      textGradient: {
        ...dataSource.textGradient,
        value: sanitize((dataSource.textGradient?.value as string) || ''),
      },
      textShadow: {
        ...dataSource.textShadow,
        value: dataSource.textShadow?.value,
      },
      items:
        dataSource.items?.map((item) => ({
          ...item,
          title: {
            ...item.title,
            value: sanitize((item.title?.value as string) || ''),
          },
        })) || [],
    };
    return {
      fields,
      params,
    };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { params };
  }
};

export default withTheme({
  defaultTheme: {
    backgroundColor: 'white',
    foregroundColor: 'black',
    darkTheme: false,
  },
  legacyBackgroundFieldName: 'background',
})(withDatasourceCheck()(IntroSection));
